import type {
  NgpRecapLaboratory,
  NgpRecapRow,
} from '../../data-access/types/gp-sampling-assignment';

export type NgpRecapLaboratoryPoints = Omit<NgpRecapLaboratory, 'samples'>;

export interface RecapFigures {
  blocked: number;
  finished: number;
  hidden: number;
  impossible: number;
  laboratories: NgpRecapLaboratoryPoints[];
  organic: number;
  pending: number;
  points: number;
  sms: number;
}

export interface RecapOrganizationGroup {
  expandable: boolean;
  figures: RecapFigures;
  id: string;
  label: string;
  rows: NgpRecapRow[];
}

export function recapPercent(done: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.round((done / total) * 100);
}

export function aggregateLaboratories(rows: NgpRecapRow[]): NgpRecapLaboratoryPoints[] {
  const byKey = new Map<number | 'none', NgpRecapLaboratoryPoints>();
  for (const row of rows) {
    for (const laboratory of row.laboratories) {
      const key = laboratory.id ?? 'none';
      const current = byKey.get(key);
      if (current) {
        current.points += laboratory.points;
      } else {
        byKey.set(key, {
          id: laboratory.id,
          name: laboratory.name,
          points: laboratory.points,
        });
      }
    }
  }
  return [...byKey.values()].sort(
    (left, right) =>
      right.points - left.points ||
      (left.name ?? '').localeCompare(right.name ?? ''),
  );
}

export function sumRecapRows(rows: NgpRecapRow[]): RecapFigures {
  const figures: RecapFigures = {
    blocked: 0,
    finished: 0,
    hidden: 0,
    impossible: 0,
    laboratories: [],
    organic: 0,
    pending: 0,
    points: 0,
    sms: 0,
  };
  for (const row of rows) {
    figures.points += row.points_total;
    figures.finished += row.points_finished;
    figures.pending += row.points_pending;
    figures.impossible += row.points_impossible;
    figures.hidden += row.points_total - row.points_visible;
    figures.sms += row.points_total - row.sms_sent;
    figures.organic += row.organic_pending;
    figures.blocked += row.organic_blocked;
  }
  figures.laboratories = aggregateLaboratories(rows);
  return figures;
}

export function groupRecapByOrganization(
  rows: NgpRecapRow[],
  labels: { noOrganization: string; unassignedSampler: string },
): RecapOrganizationGroup[] {
  const buckets = new Map<string, NgpRecapRow[]>();
  for (const row of rows) {
    const key = row.organization?.id == null ? 'none' : String(row.organization.id);
    const members = buckets.get(key);
    if (members) {
      members.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }

  const groups: RecapOrganizationGroup[] = [];
  for (const [key, members] of buckets) {
    const sorted = [...members].sort((left, right) => {
      if ((left.sampler_id == null) !== (right.sampler_id == null)) {
        return left.sampler_id == null ? -1 : 1;
      }
      return (
        right.points_total - left.points_total ||
        (left.sampler_name ?? '').localeCompare(right.sampler_name ?? '')
      );
    });
    const unassignedOnly = sorted.every((row) => row.sampler_id == null);
    const organization = sorted.find((row) => row.organization?.id != null)?.organization;
    let label = labels.noOrganization;
    if (unassignedOnly) {
      label = labels.unassignedSampler;
    } else if (organization?.name) {
      label = organization.name;
    } else if (organization?.id != null) {
      label = `#${organization.id}`;
    }
    groups.push({
      expandable: !unassignedOnly,
      figures: sumRecapRows(sorted),
      id: unassignedOnly ? 'unassigned' : `org:${key}`,
      label,
      rows: sorted,
    });
  }

  groups.sort((left, right) => {
    if (left.id === 'unassigned') {
      return -1;
    }
    if (right.id === 'unassigned') {
      return 1;
    }
    return right.figures.points - left.figures.points || left.label.localeCompare(right.label, 'fr');
  });
  return groups;
}
